import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Clock,
  Truck,
  Server,
  Database,
  CreditCard,
  Bot,
  Bell,
  ArrowLeft,
  RefreshCw,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/status`);
      const data = await res.json();
      setStatus(data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (statusValue) => {
    switch (statusValue) {
      case 'operational':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'partial_outage':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'major_outage':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'maintenance':
        return <Clock className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusColor = (statusValue) => {
    switch (statusValue) {
      case 'operational':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'degraded':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'partial_outage':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'major_outage':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'maintenance':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getServiceIcon = (serviceId) => {
    switch (serviceId) {
      case 'api':
        return Server;
      case 'database':
        return Database;
      case 'payments':
        return CreditCard;
      case 'ai_services':
        return Bot;
      case 'notifications':
        return Bell;
      default:
        return Server;
    }
  };

  const statusLabels = {
    operational: 'Opérationnel',
    degraded: 'Dégradé',
    partial_outage: 'Panne partielle',
    major_outage: 'Panne majeure',
    maintenance: 'Maintenance'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-xl font-['Chivo'] text-slate-900">Flitflow</span>
                <span className="text-sm text-slate-500 block">Status</span>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Overall Status */}
        <Card className={`mb-8 ${status?.status === 'operational' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getStatusIcon(status?.status)}
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">
                    {status?.status === 'operational' 
                      ? 'Tous les systèmes opérationnels'
                      : 'Certains systèmes rencontrent des problèmes'
                    }
                  </h1>
                  <p className="text-sm text-slate-600">
                    Uptime 90 jours: {status?.uptime_90d?.toFixed(2)}%
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchStatus}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Rafraîchir
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Active Incidents */}
        {status?.active_incidents?.length > 0 && (
          <Card className="mb-8 bg-amber-50 border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Incidents en cours
              </CardTitle>
            </CardHeader>
            <CardContent>
              {status.active_incidents.map((incident) => (
                <div key={incident.id} className="p-4 bg-white rounded-lg border border-amber-200 mb-3 last:mb-0">
                  <h4 className="font-semibold text-slate-900">{incident.title}</h4>
                  <p className="text-sm text-slate-600 mt-1">{incident.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                    <span>Sévérité: {incident.severity}</span>
                    <span>Statut: {incident.status}</span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Services Status */}
        <Card>
          <CardHeader>
            <CardTitle>État des services</CardTitle>
            <CardDescription>
              Dernière mise à jour: {lastUpdate?.toLocaleTimeString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {status?.services && Object.entries(status.services).map(([id, service]) => {
                const ServiceIcon = getServiceIcon(id);
                return (
                  <div 
                    key={id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <ServiceIcon className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{service.name}</h4>
                        <p className="text-sm text-slate-500">{service.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {service.latency_ms > 0 && (
                        <span className="text-xs text-slate-400 font-mono">
                          {service.latency_ms}ms
                        </span>
                      )}
                      <Badge variant="outline" className={getStatusColor(service.status)}>
                        {getStatusIcon(service.status)}
                        <span className="ml-2">{statusLabels[service.status]}</span>
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Resolved Incidents */}
        {status?.recent_resolved_incidents?.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Incidents récents résolus</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {status.recent_resolved_incidents.map((incident) => (
                  <div key={incident.id} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0">
                    <div>
                      <h4 className="font-medium text-slate-900">{incident.title}</h4>
                      <p className="text-xs text-slate-500">
                        Résolu le {new Date(incident.resolved_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                      Résolu
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>Flitflow Status Page • Mis à jour automatiquement toutes les minutes</p>
          <p className="mt-1">
            <a href="https://flitflow.ai" className="text-orange-500 hover:underline">flitflow.ai</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default StatusPage;
